import type { BalanceSummary, ReleaseDetail } from '../api/wallet.contract'
import { roundMoneyForDisplay } from '@shared/lib/money/display-reconciliation'
import { Money } from '@shared/lib/money/money'

type ReleaseDisplay = ReleaseDetail & {
  sources: ReleaseDetail['sources'] & { referrals: ReleaseDetail['sources']['oneTime'] }
}

export function releaseDatesForDisplay(balance: BalanceSummary): BalanceSummary['nextReleases'] {
  return balance.nextReleases.map((release) => ({
    ...release,
    amount: roundMoneyForDisplay(release.amount),
  }))
}

export function releaseDetailForDisplay(
  detail: ReleaseDetail,
  items: ReleaseDetail['items'],
  options: { itemsComplete: boolean },
): ReleaseDisplay {
  const sources = releaseSourcesFromItems(detail, options.itemsComplete ? items : detail.items)
  return {
    ...detail,
    sources,
    items,
  }
}
function releaseSourcesFromItems(
  detail: ReleaseDetail,
  items: ReleaseDetail['items'],
): ReleaseDisplay['sources'] {
  const sourceFor = (kind: ReleaseDetail['items'][number]['kind'] | 'REFERRAL') => {
    const scoped = items.filter((item) => kind === 'REFERRAL'
      ? item.isReferral === true
      : item.isReferral !== true && item.kind === kind)
    const amount = scoped.reduce(
      (total, item) => total.add(Money.fromMonetaryValue(item.amount)),
      new Money(0, detail.currency),
    )
    return { count: scoped.length, amount: amount.toMonetaryValue() }
  }

  return {
    oneTime: sourceFor('ONE_TIME'),
    subscriptionInitial: sourceFor('SUBSCRIPTION_INITIAL'),
    renewals: sourceFor('RENEWAL'),
    reserves: sourceFor('RESERVE'),
    referrals: sourceFor('REFERRAL'),
  }
}

// Agenda dates are Core UTC day keys; statement availableAt values are instants.
export function formatReleaseDate(
  value: string,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
): string {
  return new Intl.DateTimeFormat('pt-BR', { ...options, timeZone: 'UTC' }).format(new Date(value))
}
